// src/admin/pages/sold-vehicles/SoldVehicleDialog.tsx
// The one form that actually marks a car Sold and collects buyer/payment
// details — reachable from the Sold Vehicles page, optionally deep-linked
// from a Reservation via ?reservationId=.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  Description as DescriptionIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { Product, REGISTRATION_TRANSFER_STATUSES, SaleDocument } from '../../../enums/product';
import { PAYMENT_METHODS, DELIVERY_METHODS } from '../../../enums/reservation';
import { TradeInSubmission } from '../../../enums/tradeIn';
import { Employee } from '../../../enums/employee';
import DrawerFooter from '../../components/DrawerFooter';
import { SoldVehicleService } from './soldVehicleService';

export interface SoldVehicleFormValues {
  carId: string;
  buyerName: string;
  buyerPhone: string;
  buyerEmail: string;
  buyerIdNumber: string;
  buyerAddress: string;
  finalSalePrice: string;
  depositAmount: string;
  paymentMethod: string;
  deliveryMethod: string;
  deliveryDate: string;
  saleNotes: string;
  salespersonId: string;
  tradeInId: string;
  financingProvider: string;
  financingTermMonths: string;
  monthlyInstallment: string;
  registrationTransferStatus: string;
  warrantyMonths: string;
  warrantyNotes: string;
  documents: SaleDocument[];
}

interface SoldVehicleDialogProps {
  isOpen: boolean;
  eligibleCars: Product[];
  eligibleTradeIns: TradeInSubmission[];
  employees: Employee[];
  prefill: Partial<SoldVehicleFormValues> & { carLocked?: boolean } | null;
  onClose: () => void;
  onSave: (values: SoldVehicleFormValues) => Promise<void>;
}

const EMPTY_FORM: SoldVehicleFormValues = {
  carId: '', buyerName: '', buyerPhone: '', buyerEmail: '', buyerIdNumber: '', buyerAddress: '',
  finalSalePrice: '', depositAmount: '', paymentMethod: 'Cash', deliveryMethod: '', deliveryDate: '',
  saleNotes: '', salespersonId: '', tradeInId: '',
  financingProvider: '', financingTermMonths: '', monthlyInstallment: '',
  registrationTransferStatus: 'Not Started', warrantyMonths: '', warrantyNotes: '',
  documents: [],
};

const SoldVehicleDialog: React.FC<SoldVehicleDialogProps> = ({
  isOpen, eligibleCars, eligibleTradeIns, employees, prefill, onClose, onSave,
}) => {
  const [formData, setFormData] = useState<SoldVehicleFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDocFiles, setPendingDocFiles] = useState<File[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docError, setDocError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...EMPTY_FORM, ...(prefill || {}) });
      setPendingDocFiles([]);
      setDocError('');
    }
  }, [isOpen, prefill]);

  const selectedCar = useMemo(
    () => eligibleCars.find(c => c.id === formData.carId) || null,
    [eligibleCars, formData.carId]
  );

  const selectedTradeIn = useMemo(
    () => eligibleTradeIns.find(t => t.id === formData.tradeInId) || null,
    [eligibleTradeIns, formData.tradeInId]
  );

  const handleChange = (field: keyof SoldVehicleFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleDocFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setPendingDocFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingDocFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveUploadedDoc = async (docToRemove: SaleDocument) => {
    setFormData(prev => ({ ...prev, documents: prev.documents.filter(d => d.url !== docToRemove.url) }));
    await SoldVehicleService.deleteDocument(docToRemove);
  };

  const tradeInValue = selectedTradeIn?.offeredPrice || 0;
  const balanceDue = Math.max(
    0,
    (parseFloat(formData.finalSalePrice) || 0) - (parseFloat(formData.depositAmount) || 0) - tradeInValue
  );

  const isValid = formData.carId && formData.buyerName.trim() && formData.buyerPhone.trim() &&
    formData.finalSalePrice && parseFloat(formData.finalSalePrice) > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      let documents = formData.documents;
      if (pendingDocFiles.length) {
        setUploadingDocs(true);
        try {
          const uploaded = await SoldVehicleService.uploadDocuments(formData.carId, pendingDocFiles);
          documents = [...documents, ...uploaded];
        } catch (err: any) {
          setDocError(err.message || 'Failed to upload one or more documents');
          setUploadingDocs(false);
          setSaving(false);
          return;
        }
        setUploadingDocs(false);
      }
      await onSave({ ...formData, documents });
      setPendingDocFiles([]);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: 640 }, maxWidth: 640 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Record a Sale</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CarIcon sx={{ mr: 1 }} />
                    Vehicle
                  </Typography>
                  <Autocomplete
                    options={eligibleCars}
                    getOptionLabel={(c) => `${c.name} (${c.sku})`}
                    value={selectedCar}
                    disabled={!!prefill?.carLocked}
                    onChange={(_e, value) => setFormData(prev => ({ ...prev, carId: value?.id || '' }))}
                    renderInput={(params) => <TextField {...params} label="Car *" placeholder="Search by name or VIN" />}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                  {selectedCar && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {selectedCar.condition} · {selectedCar.isImported ? 'Imported' : 'Local'} · {selectedCar.branchName || 'No branch'}
                    </Typography>
                  )}
                  <TextField
                    select
                    fullWidth
                    label="Salesperson"
                    value={formData.salespersonId}
                    onChange={handleChange('salespersonId')}
                    sx={{ mt: 2 }}
                    helperText="Optional — the employee credited with this sale"
                  >
                    <MenuItem value="">None</MenuItem>
                    {employees.map((e) => (
                      <MenuItem key={e.id} value={e.id}>{e.fullName} ({e.jobRole})</MenuItem>
                    ))}
                  </TextField>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Buyer &amp; Identity
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Buyer Name *" value={formData.buyerName} onChange={handleChange('buyerName')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Buyer Phone *" value={formData.buyerPhone} onChange={handleChange('buyerPhone')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Buyer Email" value={formData.buyerEmail} onChange={handleChange('buyerEmail')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="ID / Licence / Passport No." value={formData.buyerIdNumber} onChange={handleChange('buyerIdNumber')} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Address" value={formData.buyerAddress} onChange={handleChange('buyerAddress')} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Final Sale Price *" value={formData.finalSalePrice} onChange={handleChange('finalSalePrice')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Deposit Already Paid" value={formData.depositAmount} onChange={handleChange('depositAmount')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Payment Method" value={formData.paymentMethod} onChange={handleChange('paymentMethod')}>
                        {PAYMENT_METHODS.map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Trade-In Credit"
                        value={formData.tradeInId}
                        onChange={handleChange('tradeInId')}
                        helperText={selectedTradeIn ? `Credits ${tradeInValue.toLocaleString()} toward this sale` : 'Optional — apply an accepted trade-in as part payment'}
                      >
                        <MenuItem value="">None</MenuItem>
                        {eligibleTradeIns.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.carYear} {t.carMake} {t.carModel} — {t.ownerName} ({(t.offeredPrice || 0).toLocaleString()})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {formData.paymentMethod === 'Financing' && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Financing Provider" value={formData.financingProvider} onChange={handleChange('financingProvider')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth type="number" label="Loan Term (months)" value={formData.financingTermMonths} onChange={handleChange('financingTermMonths')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth type="number" label="Monthly Installment" value={formData.monthlyInstallment} onChange={handleChange('monthlyInstallment')} />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Balance Due (price − deposit − trade-in credit)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {balanceDue.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Delivery &amp; Registration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Delivery Method" value={formData.deliveryMethod} onChange={handleChange('deliveryMethod')}>
                        <MenuItem value="">Not set</MenuItem>
                        {DELIVERY_METHODS.map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Delivery Date"
                        value={formData.deliveryDate}
                        onChange={handleChange('deliveryDate')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Registration Transfer" value={formData.registrationTransferStatus} onChange={handleChange('registrationTransferStatus')}>
                        {REGISTRATION_TRANSFER_STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Warranty (months)" value={formData.warrantyMonths} onChange={handleChange('warrantyMonths')} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Warranty Notes" value={formData.warrantyNotes} onChange={handleChange('warrantyNotes')} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DescriptionIcon sx={{ mr: 1 }} />
                    Related Documents
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Attach the title deed, invoice, buyer's ID copy, warranty card, or any other file related to this sale.
                  </Typography>

                  {docError && (
                    <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{docError}</Typography>
                  )}

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    disabled={!formData.carId || uploadingDocs}
                  >
                    Add Document
                    <input type="file" hidden multiple onChange={handleDocFilesSelected} />
                  </Button>
                  {!formData.carId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Select a car first before attaching documents.
                    </Typography>
                  )}

                  {(formData.documents.length > 0 || pendingDocFiles.length > 0) && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {formData.documents.map((doc) => (
                        <Box
                          key={doc.url}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <FileIcon fontSize="small" color="action" />
                            <Typography
                              component="a"
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ color: 'primary.main', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {doc.name}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => handleRemoveUploadedDoc(doc)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      {pendingDocFiles.map((file, idx) => (
                        <Box
                          key={`pending-${idx}-${file.name}`}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <FileIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">(pending upload)</Typography>
                          </Box>
                          <IconButton size="small" onClick={() => handleRemovePendingFile(idx)} disabled={uploadingDocs}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <TextField fullWidth multiline rows={3} label="Sale Notes" value={formData.saleNotes} onChange={handleChange('saleNotes')} />
                </CardContent>
              </Card>
            </Box>

            <DrawerFooter>
              <Button onClick={onClose} variant="outlined" disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !isValid}
                startIcon={saving && <CircularProgress size={16} />}
              >
                {saving ? 'Saving...' : 'Record Sale'}
              </Button>
            </DrawerFooter>
          </Box>
        </Box>
      </Drawer>

      <Backdrop
        open={saving}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, minWidth: 280, boxShadow: 24 }}>
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Recording Sale…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default SoldVehicleDialog;
